/**
 * Aprovacao do Challenge da Global (spec 064, modulo `nelogica-v2`), dentro de Solicitacoes: o cliente pede pela area,
 * o administrador aprova (com a taxa de ativacao do plano, que pode mudar ou dispensar) ou recusa com o motivo; plano
 * com taxa espera o pagamento; aprovado e pago, o administrador cria a conta Funded na extranet e registra aqui. A fila
 * "Funded a liberar" e o dialogo de liberar moram aqui tambem. Prazos, atraso, valores e recusas vem do backend.
 */
import {
  Aviso, Botao, Campo, CampoCaixa, CampoData, CampoDinheiro, CampoTextoLongo, Carregando, DePara, Dialogo, Fatos, LinkInterno, Pilha, Secao, Selo, Tabela, Vazio,
  formatarDataHora, formatarDia, formatarReais, hojeEmSaoPaulo, soDigitos,
} from "@mtc/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type FormEvent } from "react";
import { STATUS_DA_SOLICITACAO, erroDe, useAcao, useApi, usePapel, type DadosDaAprovacao, type DetalheDaMatricula, type FundedALiberar, type ModuloContaNaCorretora, type SolicitacaoPainel } from "../api";
import { ComprovanteDoAlvo } from "./Comprovantes";
import { useTaxasDeAtivacao } from "./Planos";
import { prazoEmPalavras } from "./Solicitacoes";

const dadosDe = (s: SolicitacaoPainel) => s.dados as DadosDaAprovacao;
const contaDoChallenge = (m?: DetalheDaMatricula) => (m?.modulos.corretora as ModuloContaNaCorretora | undefined)?.conta ?? null;

export function useFundedALiberar(ligado = true) {
  const { ler } = useApi();
  return useQuery({ queryKey: ["funded-a-liberar"], queryFn: () => ler<{ itens: FundedALiberar[] }>("/v1/admin/aprovacoes/a-liberar"), enabled: ligado });
}

const seloDoPrazo = (p: { vence_em: string | null; prazo: string | null; atrasado: boolean }) => {
  const x = prazoEmPalavras(p);
  return <Selo tom={x.tom === "erro" ? "erro" : x.tom === "alerta" ? "alerta" : "neutro"}>{x.texto}</Selo>;
};

// ---------------------------------------------------------------- a fila "Funded a liberar"

export function FilaDeFunded() {
  const { pode } = usePapel();
  const q = useFundedALiberar();
  const [abrindo, setAbrindo] = useState<FundedALiberar>();
  const [feito, setFeito] = useState<string>();
  if (q.isLoading) return <Carregando compacto texto="Buscando as contas a liberar" />;
  if (q.isError) return <Aviso tipo="erro">{erroDe(q.error)}</Aviso>;
  const itens = q.data?.itens ?? [];
  return (
    <Pilha espaco={4}>
      {feito && <Aviso tipo="sucesso">{feito}</Aviso>}
      <Tabela
        linhas={itens}
        chave={(a) => a.solicitacao_id}
        vazio={<Vazio titulo="Nada a liberar">Todo Challenge aprovado já tem a conta Funded registrada.</Vazio>}
        colunas={[
          { chave: "cliente", rotulo: "Cliente", celula: (a) => <><LinkInterno href={`/solicitacoes/${a.solicitacao_id}`}>{a.cliente}</LinkInterno><span className="mtc-tabela__apoio mtc-mono">{a.cpf}</span><span className="mtc-tabela__apoio">{a.plano}</span></> },
          { chave: "conta", rotulo: "Conta do Challenge", celula: (a) => (a.conta_do_challenge ? <span className="mtc-mono">{a.conta_do_challenge}</span> : <span className="mtc-texto-2">Sem conta</span>) },
          { chave: "taxa", rotulo: "Taxa de ativação", celula: (a) => (a.taxa_de_ativacao_centavos ? <><span className="mtc-mono">{formatarReais(a.taxa_de_ativacao_centavos)}</span><span className="mtc-tabela__apoio">paga</span></> : <span className="mtc-texto-2">Sem taxa</span>) },
          { chave: "prazo", rotulo: "Prazo", celula: (a) => <>{seloDoPrazo(a)}<span className="mtc-tabela__apoio">Aprovado em {formatarDataHora(a.aprovada_em)}</span></> },
          { chave: "acao", rotulo: "Ações", rotuloOculto: true, celula: (a) => <Botao tamanho="pequeno" disabled={!pode("SUPORTE")} aria-label={`Liberar conta Funded de ${a.cliente}`} onClick={() => { setFeito(undefined); setAbrindo(a); }}>Liberar conta Funded</Botao> },
        ]}
      />
      {abrindo && <DialogoLiberarFunded alvo={abrindo} aoFechar={() => setAbrindo(undefined)} aoPronto={(t) => { setAbrindo(undefined); setFeito(t); }} />}
    </Pilha>
  );
}

/**
 * Liberar a conta Funded (064, 2.5): o numero da conta nova que o administrador criou na extranet e o dia. Antes de
 * gravar, a conferencia mostra a conta do Challenge dando lugar a conta Funded. As regras da conta (outra, valida, sem
 * outro plano vivo) sao do backend: a frase dele aparece aqui.
 */
export function DialogoLiberarFunded({ alvo, aoFechar, aoPronto }: { alvo: Pick<FundedALiberar, "solicitacao_id" | "cliente" | "cpf" | "plano" | "conta_do_challenge">; aoFechar: () => void; aoPronto: (texto: string) => void }) {
  const { postar } = useApi();
  const acao = useAcao();
  const qc = useQueryClient();
  const [conta, setConta] = useState("");
  const [dia, setDia] = useState(hojeEmSaoPaulo());
  const [conferir, setConferir] = useState(false);
  const pronta = conta.length > 0 && Boolean(dia);
  const liberar = async () => {
    const ok = await acao.rodar(() => postar(`/v1/admin/solicitacoes/${alvo.solicitacao_id}/liberacao-funded`, { conta, dia }));
    if (!ok) return;
    await qc.invalidateQueries();
    aoPronto(`Conta Funded ${conta} liberada para ${alvo.cliente}, desde ${formatarDia(dia)}. A etapa virou Funded, o cliente foi avisado e a taxa de manutenção começou a contar.`);
  };
  const rodape = conferir ? (
    <>
      <Botao variante="fantasma" onClick={() => { acao.limpar(); setConferir(false); }}>Voltar</Botao>
      <Botao carregando={acao.carregando} onClick={() => void liberar()}>Liberar conta Funded</Botao>
    </>
  ) : (
    <>
      <Botao variante="fantasma" onClick={aoFechar}>Cancelar</Botao>
      <Botao disabled={!pronta} onClick={() => { acao.limpar(); setConferir(true); }}>Conferir</Botao>
    </>
  );
  return (
    <Dialogo
      aberto
      aoFechar={aoFechar}
      titulo={conferir ? "Confere antes de liberar" : "Liberar conta Funded"}
      descricao={`${alvo.cliente}, ${alvo.plano}. ${conferir ? "Ao liberar, a etapa vira Funded neste dia, o cliente é avisado e a taxa de manutenção começa a contar." : "Crie a conta Funded na extranet da Nelogica e informe aqui o número dela e o dia em que foi criada."}`}
      rodape={rodape}
    >
      <div className="mtc-pilha" style={{ gap: "var(--e-4)" }}>
        {acao.erro && <Aviso tipo="erro">{acao.erro}</Aviso>}
        {conferir ? (
          <>
            <DePara rotulo="Troca de conta" mono de={{ rotulo: "Conta do Challenge", valor: alvo.conta_do_challenge ?? "Sem conta" }} para={{ rotulo: "Conta Funded", valor: conta, detalhe: "Criada na extranet" }} />
            <Fatos itens={[{ rotulo: "CPF", valor: alvo.cpf, mono: true }, { rotulo: "Liberada em", valor: formatarDia(dia) }]} />
            <Aviso tipo="info">Confira na extranet que a conta {conta} é deste CPF. A conta do Challenge fica na trilha do plano.</Aviso>
          </>
        ) : (
          <>
            <Campo rotulo="Conta Funded" valor={conta} aoMudar={(v) => setConta(soDigitos(v).slice(0, 12))} inputMode="numeric" mono autoComplete="off" dica={alvo.conta_do_challenge ? `Só números, como está na extranet. É uma conta nova, diferente da do Challenge (${alvo.conta_do_challenge}).` : "Só números, como está na extranet."} />
            <CampoData rotulo="Liberada em" valor={dia} aoMudar={setDia} max={hojeEmSaoPaulo()} dica="Hoje, ou o dia em que você criou a conta na extranet. Não aceita data futura." />
          </>
        )}
      </div>
    </Dialogo>
  );
}

// ---------------------------------------------------------------- o pedido, em cada passo

/** O bloco de decisao do pedido de aprovacao, pelo status e pelos dados que o backend gravou. */
export function DecisaoDaAprovacao({ s, m, aoDecidir }: { s: SolicitacaoPainel; m?: DetalheDaMatricula; aoDecidir: (t: string) => void }) {
  const d = dadosDe(s);
  if (s.status === "PENDENTE") return <DecidirAprovacao s={s} m={m} aoDecidir={aoDecidir} />;
  if (s.status === "AGUARDANDO_PAGAMENTO") return <PagamentoDaAprovacao s={s} aoDecidir={aoDecidir} />;
  if (s.status === "APROVADA" && d.aguardando_liberacao) return <LiberarFundedDoPedido s={s} m={m} aoDecidir={aoDecidir} />;
  if (s.status === "APROVADA" && d.liberacao) return <FundedLiberada s={s} />;
  return null;
}

function useDepois(aoDecidir: (t: string) => void) {
  const qc = useQueryClient();
  return async (texto: string) => {
    await qc.invalidateQueries();
    aoDecidir(texto);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
}

function DecidirAprovacao({ s, m, aoDecidir }: { s: SolicitacaoPainel; m?: DetalheDaMatricula; aoDecidir: (t: string) => void }) {
  const { postar } = useApi();
  const { pode } = usePapel();
  const depois = useDepois(aoDecidir);
  const acao = useAcao();
  const d = dadosDe(s);
  const conta = contaDoChallenge(m);
  // A taxa do catalogo preenche o campo (064, 2.4); o administrador muda so neste pedido, ou dispensa.
  const taxas = useTaxasDeAtivacao();
  const doCatalogo = taxas.data ? (taxas.data.itens.find((t) => t.plano_slug === s.matricula.plano.slug)?.valor_centavos ?? null) : undefined;
  const [valor, setValor] = useState(0);
  const [mexido, setMexido] = useState(false);
  const [dispensar, setDispensar] = useState(false);
  const [conferir, setConferir] = useState(false);
  const [recusar, setRecusar] = useState(false);
  useEffect(() => { if (!mexido && doCatalogo != null) setValor(doCatalogo); }, [doCatalogo, mexido]);
  const cobra = !dispensar && valor > 0;
  // Sem mexer, vai sem valor e o backend usa o do plano; mexeu ou dispensou, vai o que esta na tela (0 dispensa).
  const corpo = dispensar ? { valor_centavos: 0 } : mexido ? { valor_centavos: valor } : {};
  const aprovar = async () => {
    let r: SolicitacaoPainel | undefined;
    const ok = await acao.rodar(async () => { r = await postar<SolicitacaoPainel>(`/v1/admin/solicitacoes/${s.id}/aprovar`, corpo); });
    if (!ok) return;
    setConferir(false);
    await depois(
      r?.status === "AGUARDANDO_PAGAMENTO"
        ? `Challenge aprovado com a taxa de ativação de ${formatarReais(r.valor_centavos ?? valor)}. O cliente foi avisado pra pagar pela área; a conta Funded espera o pagamento.`
        : "Challenge aprovado. O pedido entrou na aba Funded a liberar: crie a conta Funded na extranet e registre aqui.",
    );
  };
  const valorNaTela = cobra ? formatarReais(valor) : dispensar ? "Dispensada neste pedido" : "Sem taxa";
  return (
    <Secao destaque rotulo="Decisão" titulo="Challenge para Funded">
      <Fatos
        itens={[
          { rotulo: "Conta do Challenge", valor: conta ?? "Sem conta", mono: Boolean(conta) },
          ...(d.ciente_de_pausar_em ? [{ rotulo: "Pausa confirmada", valor: formatarDataHora(d.ciente_de_pausar_em), detalhe: "O cliente confirmou que pausou as operações até a análise" }] : []),
          ...(d.analise_vence_em ? [{ rotulo: "Decidir até", valor: formatarDataHora(d.analise_vence_em), detalhe: "Prazo do regulamento pra análise" }] : []),
        ]}
      />
      <ol className="mtc-passos">
        <li>Confira na corretora o resultado da conta do Challenge{conta ? <> <strong className="mtc-mono">{conta}</strong></> : null} e as regras do plano.</li>
        <li>Aprove ou recuse. Recusando, escreva o motivo: o cliente lê e pode pedir de novo.</li>
        <li>{cobra ? "Com taxa de ativação, o cliente paga pela área e só então o pedido vai pra aba Funded a liberar." : "Sem taxa, o pedido vai direto pra aba Funded a liberar."} Lá você cria a conta Funded na extranet e registra o número.</li>
      </ol>
      <form className="mtc-formulario" onSubmit={(ev: FormEvent) => { ev.preventDefault(); acao.limpar(); setConferir(true); }} noValidate>
        {acao.erro && !conferir && <Aviso tipo="erro">{acao.erro}</Aviso>}
        {!pode("SUPORTE") && <Aviso tipo="info">Decidir solicitações é do suporte pra cima.</Aviso>}
        <CampoDinheiro
          rotulo="Taxa de ativação"
          centavos={dispensar ? 0 : valor}
          aoMudar={(v) => { setMexido(true); setValor(v); }}
          disabled={dispensar}
          dica={taxas.isLoading ? "Buscando a taxa do plano." : doCatalogo != null ? `A do plano é ${formatarReais(doCatalogo)} (em Planos). Mudar aqui vale só para este pedido.` : "Este plano não tem taxa em Planos. Preencha só se este pedido for cobrar."}
        />
        <CampoCaixa marcado={dispensar} aoMudar={setDispensar}>Dispensar a taxa de ativação neste pedido</CampoCaixa>
        <div className="mtc-linha" style={{ gap: "var(--e-2)" }}>
          <Botao type="submit" disabled={!pode("SUPORTE")}>{cobra ? "Aprovar e pedir a taxa" : "Aprovar o Challenge"}</Botao>
          <Botao variante="secundario" onClick={() => setRecusar(true)} disabled={!pode("SUPORTE")}>Recusar</Botao>
        </div>
      </form>
      <Dialogo
        aberto={conferir}
        aoFechar={() => setConferir(false)}
        titulo="Aprovar o Challenge"
        descricao={cobra ? "O cliente recebe o aviso pra pagar a taxa de ativação. A conta Funded é liberada depois do pagamento." : "O pedido vai pra aba Funded a liberar. A etapa muda quando você registrar a conta Funded."}
        rodape={<><Botao variante="fantasma" onClick={() => setConferir(false)}>Voltar</Botao><Botao carregando={acao.carregando} onClick={() => void aprovar()}>Aprovar</Botao></>}
      >
        <div className="mtc-pilha" style={{ gap: "var(--e-3)" }}>
          {acao.erro && <Aviso tipo="erro">{acao.erro}</Aviso>}
          <Fatos
            itens={[
              { rotulo: "Cliente", valor: s.pessoa.nome, detalhe: s.pessoa.cpf },
              { rotulo: "Plano", valor: s.matricula.plano.nome },
              { rotulo: "Taxa de ativação", valor: valorNaTela, mono: cobra },
              { rotulo: "Depois", valor: cobra ? "O cliente paga pela área" : "Funded a liberar", detalhe: cobra ? "PIX ou cartão, ou o PIX de recebimento com o comprovante" : "Criar a conta Funded na extranet" },
            ]}
          />
        </div>
      </Dialogo>
      <RecusarAprovacao s={s} aberto={recusar} aoFechar={() => setRecusar(false)} aoConcluir={() => depois("Pedido de aprovação recusado. O cliente foi avisado com o motivo e pode pedir de novo.")} />
    </Secao>
  );
}

function PagamentoDaAprovacao({ s, aoDecidir }: { s: SolicitacaoPainel; aoDecidir: (t: string) => void }) {
  const { postar } = useApi();
  const { pode } = usePapel();
  const depois = useDepois(aoDecidir);
  const acao = useAcao();
  const [conferir, setConferir] = useState(false);
  const [recusar, setRecusar] = useState(false);
  // O comprovante chegou por outro canal: o administrador confirma por fora, com a observacao.
  const [porFora, setPorFora] = useState(false);
  const [observacao, setObservacao] = useState("");
  const pg = s.pagamento;
  const pago = Boolean(pg);
  const pronta = pago || (porFora && observacao.trim().length >= 5);
  const confirmar = async () => {
    const corpo = !pago && porFora ? { por_fora: { observacao: observacao.trim() } } : {};
    const ok = await acao.rodar(() => postar(`/v1/admin/solicitacoes/${s.id}/confirmar-pagamento`, corpo));
    if (!ok) return;
    setConferir(false);
    await depois("Taxa de ativação confirmada. O pedido entrou na aba Funded a liberar: crie a conta Funded na extranet e registre aqui.");
  };
  return (
    <Secao destaque rotulo="Decisão" titulo={pago ? "Taxa paga: confirmar" : "Aguardando a taxa de ativação"}>
      <Fatos
        itens={[
          { rotulo: "Taxa de ativação", valor: s.valor_centavos != null ? formatarReais(s.valor_centavos) : "Sem valor", mono: true },
          { rotulo: "Pagamento", valor: pg ? (pg.como === "PLATAFORMA" ? `Pela plataforma${pg.meio ? ` (${pg.meio === "pix" ? "PIX" : "cartão"})` : ""}` : "Por fora") : "Ainda não pago", detalhe: pg ? `${formatarDataHora(pg.pago_em)}${pg.pedido ? `, pedido ${pg.pedido}` : ""}` : "O cliente paga pela área: por PIX ou cartão confirma sozinho; pelo PIX de recebimento, conferir o comprovante em Comprovantes confirma o pagamento." },
        ]}
      />
      {pg?.observacao && <Aviso tipo="info">Observação do pagamento por fora: {pg.observacao}</Aviso>}
      <ComprovanteDoAlvo pessoaId={s.pessoa.id} tipo="PEDIDO" id={s.id} aviso />
      <form className="mtc-formulario" onSubmit={(ev: FormEvent) => { ev.preventDefault(); acao.limpar(); setConferir(true); }} noValidate>
        {!pago && (
          <Pilha espaco={3}>
            <CampoCaixa marcado={porFora} aoMudar={setPorFora}>Recebi o comprovante por fora (WhatsApp) e confirmo o pagamento</CampoCaixa>
            {porFora && <CampoTextoLongo rotulo="Observação do pagamento" valor={observacao} aoMudar={setObservacao} maxLength={500} required placeholder="Ex.: comprovante do PIX de R$ 497,00 recebido pelo WhatsApp em 31/10." dica="Pelo menos 5 letras. Fica na trilha do pedido." />}
          </Pilha>
        )}
        <div className="mtc-linha" style={{ gap: "var(--e-2)" }}>
          <Botao type="submit" disabled={!pode("SUPORTE") || !pronta}>Confirmar o pagamento</Botao>
          <Botao variante="secundario" onClick={() => setRecusar(true)} disabled={!pode("SUPORTE")}>Recusar</Botao>
        </div>
      </form>
      <Dialogo
        aberto={conferir}
        aoFechar={() => setConferir(false)}
        titulo="Confirmar a taxa de ativação"
        descricao="O pedido passa pra aba Funded a liberar. A etapa muda quando você registrar a conta Funded."
        rodape={<><Botao variante="fantasma" onClick={() => setConferir(false)}>Voltar</Botao><Botao carregando={acao.carregando} onClick={() => void confirmar()}>{pago ? "Confirmar" : "Confirmar o pagamento por fora"}</Botao></>}
      >
        <div className="mtc-pilha" style={{ gap: "var(--e-3)" }}>
          {acao.erro && <Aviso tipo="erro">{acao.erro}</Aviso>}
          <Fatos
            itens={[
              { rotulo: "Cliente", valor: s.pessoa.nome, detalhe: s.pessoa.cpf },
              ...(s.valor_centavos != null ? [{ rotulo: pago ? "Valor pago" : "Valor recebido por fora", valor: formatarReais(s.valor_centavos), mono: true }] : []),
              { rotulo: "Depois", valor: "Funded a liberar", detalhe: "Criar a conta Funded na extranet" },
            ]}
          />
        </div>
      </Dialogo>
      <RecusarAprovacao s={s} aberto={recusar} aoFechar={() => setRecusar(false)} aoConcluir={() => depois("Pedido de aprovação recusado. O cliente foi avisado com o motivo.")} />
    </Secao>
  );
}

function LiberarFundedDoPedido({ s, m, aoDecidir }: { s: SolicitacaoPainel; m?: DetalheDaMatricula; aoDecidir: (t: string) => void }) {
  const { pode } = usePapel();
  const d = dadosDe(s);
  // O atraso e a conta do Challenge vem da fila (o backend conta o prazo); fora dela, os dados do pedido.
  const fila = useFundedALiberar();
  const item = fila.data?.itens.find((i) => i.solicitacao_id === s.id);
  const conta = item?.conta_do_challenge ?? contaDoChallenge(m);
  const [aberto, setAberto] = useState(false);
  const qc = useQueryClient();
  return (
    <Secao destaque rotulo="Decisão" titulo="Liberar a conta Funded" acoes={item ? seloDoPrazo(item) : undefined}>
      <Fatos
        itens={[
          { rotulo: "Aprovado em", valor: s.decidida_em ? formatarDataHora(s.decidida_em) : "", detalhe: s.valor_centavos ? `Taxa de ativação de ${formatarReais(s.valor_centavos)} paga` : "Sem taxa de ativação" },
          { rotulo: "Conta do Challenge", valor: conta ?? "Sem conta", mono: Boolean(conta) },
          ...(d.prazo ? [{ rotulo: "Prazo prometido ao cliente", valor: d.prazo, detalhe: d.vence_em ? `Até ${formatarDataHora(d.vence_em)}` : undefined }] : []),
        ]}
      />
      <ol className="mtc-passos">
        <li>Na extranet da Nelogica, crie a conta Funded do CPF <strong className="mtc-mono">{s.pessoa.cpf}</strong>.</li>
        <li>Registre aqui o número da conta nova e o dia em que ela foi criada. A etapa vira Funded nesse dia e a taxa de manutenção começa a contar.</li>
      </ol>
      {!pode("SUPORTE") && <Aviso tipo="info">Liberar a conta é do suporte pra cima.</Aviso>}
      <div><Botao disabled={!pode("SUPORTE")} onClick={() => setAberto(true)}>Liberar conta Funded</Botao></div>
      {aberto && (
        <DialogoLiberarFunded
          alvo={{ solicitacao_id: s.id, cliente: s.pessoa.nome, cpf: s.pessoa.cpf, plano: s.matricula.plano.nome, conta_do_challenge: conta }}
          aoFechar={() => setAberto(false)}
          aoPronto={(t) => { setAberto(false); void qc.invalidateQueries(); aoDecidir(t); window.scrollTo({ top: 0, behavior: "smooth" }); }}
        />
      )}
    </Secao>
  );
}

function FundedLiberada({ s }: { s: SolicitacaoPainel }) {
  const l = dadosDe(s).liberacao!;
  return (
    <Secao rotulo="Liberada" titulo="Conta Funded liberada" acoes={<Selo tom="sucesso">Funded</Selo>}>
      <DePara rotulo="Troca de conta" mono de={{ rotulo: "Conta do Challenge", valor: l.conta_anterior ?? "Sem conta" }} para={{ rotulo: "Conta Funded", valor: l.conta, detalhe: `Desde ${formatarDia(l.dia)}` }} />
    </Secao>
  );
}

/** Recusar com o motivo (3 letras no minimo, como o backend pede): vai pro cliente no aviso e no e-mail. */
function RecusarAprovacao({ s, aberto, aoFechar, aoConcluir }: { s: SolicitacaoPainel; aberto: boolean; aoFechar: () => void; aoConcluir: () => void }) {
  const { postar } = useApi();
  const acao = useAcao();
  const [texto, setTexto] = useState("");
  useEffect(() => { if (aberto) { setTexto(""); acao.limpar(); } }, [aberto, acao.limpar]);
  const enviar = async (ev: FormEvent) => {
    ev.preventDefault();
    const ok = await acao.rodar(() => postar(`/v1/admin/solicitacoes/${s.id}/recusar`, { observacao: texto.trim() }));
    if (!ok) return;
    aoFechar();
    aoConcluir();
  };
  return (
    <Dialogo aberto={aberto} aoFechar={aoFechar} titulo="Recusar o pedido" descricao={`${s.pessoa.nome}, ${s.matricula.plano.nome}. O motivo vai para o cliente, no aviso e no e-mail.`}>
      <form className="mtc-formulario" onSubmit={enviar} noValidate>
        {acao.erro && <Aviso tipo="erro">{acao.erro}</Aviso>}
        <CampoTextoLongo rotulo="Motivo para o cliente" valor={texto} aoMudar={setTexto} maxLength={1000} required placeholder="Ex.: a meta de lucro ainda não foi batida no Challenge." />
        <div className="mtc-dialogo__rodape">
          <Botao variante="fantasma" onClick={aoFechar}>Cancelar</Botao>
          <Botao type="submit" variante="perigo" carregando={acao.carregando} disabled={texto.trim().length < 3}>Recusar</Botao>
        </div>
      </form>
    </Dialogo>
  );
}

// ---------------------------------------------------------------- na matricula

/** O resumo `aprovacao` que o backend manda na matricula da Global (spec 064): se pode pedir e o pedido de agora. */
export type ResumoDaAprovacao = {
  pode_pedir: boolean;
  motivo: string | null;
  prazo_da_analise: string;
  pedido: { id: string; status: SolicitacaoPainel["status"]; criado_em: string; valor_centavos: number | null; pago: boolean; aguardando_liberacao: boolean; prazo: string | null; observacao: string | null; liberada_no_dia: string | null } | null;
};

/** O pedido de aprovacao do Challenge na ficha do plano: o ultimo pedido, com o caminho pra ele, ou se o cliente pode pedir. */
export function AprovacaoDaMatricula({ a }: { a: ResumoDaAprovacao }) {
  const p = a.pedido;
  const st = p ? STATUS_DA_SOLICITACAO[p.status] : null;
  const passo = !p ? null : p.liberada_no_dia ? `Conta Funded liberada em ${formatarDia(p.liberada_no_dia)}` : p.aguardando_liberacao ? "Aprovado, esperando a conta Funded" : p.status === "AGUARDANDO_PAGAMENTO" ? `Aprovado, esperando a taxa de ativação${p.valor_centavos ? ` de ${formatarReais(p.valor_centavos)}` : ""}` : null;
  return (
    <Secao rotulo="Aprovação do Challenge" titulo={p ? "Pedido de aprovação" : "Sem pedido de aprovação"} acoes={st ? <Selo tom={st.tom}>{st.rotulo}</Selo> : undefined}>
      {p ? (
        <Fatos
          itens={[
            { rotulo: "Pedido em", valor: formatarDataHora(p.criado_em), detalhe: <LinkInterno href={`/solicitacoes/${p.id}`}>Abrir o pedido</LinkInterno> },
            ...(passo ? [{ rotulo: "Agora", valor: passo }] : []),
            ...(p.status === "RECUSADA" && p.observacao ? [{ rotulo: "Motivo da recusa", valor: p.observacao }] : []),
          ]}
        />
      ) : null}
      <p className="mtc-texto-2" style={{ margin: 0, fontSize: "var(--t-sm)" }}>{a.pode_pedir ? `O cliente pode pedir a aprovação pela área. A análise leva até ${a.prazo_da_analise}.` : a.motivo}</p>
    </Secao>
  );
}
